import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { apiFetch } from '../lib/apiFetch';
import { useApiRoot } from '../contexts/ApiRootContext';

export type FeedbackRating = 'up' | 'down';
export type FeedbackVerdict = 'good' | 'bad';

export interface FeedbackField {
  /** Stable identifier stored with the feedback, e.g. `suggested_title` or `result:<recordId>`. */
  key: string;
  label: string;
  /** What the user was shown for this field. */
  value?: string | null;
  group?: string;
  meta?: Record<string, string | number | boolean | null>;
  /** Placeholder for the comment box when the field is marked bad. */
  badPrompt?: string;
  /** Ask which record the user expected when this field is marked bad. */
  asksExpectedRecord?: boolean;
}

interface FieldSelection {
  key: string;
  verdict: FeedbackVerdict;
  comment: string;
}

interface FeedbackThumbsProps {
  surface: 'upload' | 'search';
  targetId: string;
  fields: FeedbackField[];
  prompt?: string;
  placement?: 'top' | 'bottom';
  align?: 'left' | 'right';
}

// Keep in sync with the limits in gcp-ara-api src/ops/feedback.py.
const MAX_FIELDS = 20;
const MAX_TEXT_LENGTH = 4000;

const OPTION_VALUE_PREVIEW_LENGTH = 60;
const RECORD_PATH_PATTERN = /\/records\/([^/?#]+)/;

function parseRecordId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  return trimmed.match(RECORD_PATH_PATTERN)?.[1] ?? trimmed;
}

function optionText(field: FeedbackField): string {
  if (!field.value) return field.label;
  const preview =
    field.value.length > OPTION_VALUE_PREVIEW_LENGTH
      ? `${field.value.slice(0, OPTION_VALUE_PREVIEW_LENGTH)}…`
      : field.value;
  return `${field.label}: ${preview}`;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string') return body.error;
  } catch {
    /* fall through to the generic message */
  }
  return `Request failed (${response.status})`;
}

/**
 * Thumbs up/down for an AI-assisted result. A click saves the vote straight away,
 * then offers an optional popover to highlight fields and suggest improvements.
 * Render with `key={targetId}` so the state resets when the target changes.
 */
export default function FeedbackThumbs({
  surface,
  targetId,
  fields,
  prompt = 'Helpful?',
  placement = 'bottom',
  align = 'right',
}: FeedbackThumbsProps) {
  const { getHref } = useApiRoot();
  const createHref = getHref('feedback');
  const location = useLocation();
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  const [selfHref, setSelfHref] = useState<string | null>(null);
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [open, setOpen] = useState(false);
  const [selections, setSelections] = useState<FieldSelection[]>([]);
  const [improvement, setImprovement] = useState('');
  const [expectedRecord, setExpectedRecord] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const fieldsByKey = useMemo(() => new Map(fields.map((field) => [field.key, field])), [fields]);

  const groupedOptions = useMemo(() => {
    const selectedKeys = new Set(selections.map((selection) => selection.key));
    const groups = new Map<string, FeedbackField[]>();
    for (const field of fields) {
      if (selectedKeys.has(field.key)) continue;
      const group = field.group ?? 'Other';
      groups.set(group, [...(groups.get(group) ?? []), field]);
    }
    return [...groups.entries()];
  }, [fields, selections]);

  const asksExpectedRecord = selections.some(
    (selection) =>
      selection.verdict === 'bad' && fieldsByKey.get(selection.key)?.asksExpectedRecord,
  );

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!createHref) return null;

  const patchFeedback = async (href: string, body: Record<string, unknown>) => {
    const response = await apiFetch(href, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await readError(response));
  };

  const handleRate = async (next: FeedbackRating) => {
    if (pending) return;
    setError(null);
    setSent(false);
    setOpen(true);
    if (next === rating) return;

    const previous = rating;
    setRating(next);
    setPending(true);
    try {
      if (selfHref) {
        await patchFeedback(selfHref, { overall_rating: next });
      } else {
        const response = await apiFetch(createHref, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            surface,
            target_id: targetId,
            route: `${location.pathname}${location.search}`,
            overall_rating: next,
          }),
        });
        if (!response.ok) throw new Error(await readError(response));
        const body = (await response.json()) as { _links: { self: { href: string } } };
        setSelfHref(body._links.self.href);
      }
    } catch (err) {
      setRating(previous);
      setError(err instanceof Error ? err.message : 'Failed to save feedback');
    } finally {
      setPending(false);
    }
  };

  const handleSend = async () => {
    if (!selfHref || pending) return;
    setError(null);
    setPending(true);
    try {
      await patchFeedback(selfHref, {
        fields: selections.flatMap((selection) => {
          const field = fieldsByKey.get(selection.key);
          if (!field) return [];
          return [
            {
              key: field.key,
              label: field.label,
              verdict: selection.verdict,
              displayed_value: field.value ?? null,
              comment: selection.verdict === 'bad' ? selection.comment : null,
              meta: field.meta ?? null,
            },
          ];
        }),
        improvement,
        expected: asksExpectedRecord ? { record_id: parseRecordId(expectedRecord) } : null,
      });
      setSent(true);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send feedback');
    } finally {
      setPending(false);
    }
  };

  const addSelection = (key: string) => {
    if (!key) return;
    setSelections((prev) => [
      ...prev,
      { key, verdict: rating === 'up' ? 'good' : 'bad', comment: '' },
    ]);
  };

  const updateSelection = (key: string, changes: Partial<FieldSelection>) => {
    setSelections((prev) =>
      prev.map((selection) => (selection.key === key ? { ...selection, ...changes } : selection)),
    );
  };

  const removeSelection = (key: string) => {
    setSelections((prev) => prev.filter((selection) => selection.key !== key));
  };

  const thumbClass = (value: FeedbackRating) => {
    const selected = rating === value;
    const selectedStyle =
      value === 'up' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600';
    return `flex h-7 w-7 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed ${
      selected ? selectedStyle : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
    }`;
  };

  const verdictClass = (selected: boolean, verdict: FeedbackVerdict) => {
    const selectedStyle =
      verdict === 'good' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600';
    return `flex h-6 w-6 items-center justify-center rounded transition-colors ${
      selected ? selectedStyle : 'text-gray-300 hover:bg-gray-100 hover:text-gray-600'
    }`;
  };

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white py-0.5 pl-3 pr-1 text-sm text-gray-500">
        <span className="mr-1">{sent ? 'Thanks for the feedback' : prompt}</span>
        <button
          type="button"
          aria-label="Thumbs up"
          aria-pressed={rating === 'up'}
          aria-controls={panelId}
          disabled={pending}
          onClick={() => void handleRate('up')}
          className={thumbClass('up')}
        >
          <ThumbsUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Thumbs down"
          aria-pressed={rating === 'down'}
          aria-controls={panelId}
          disabled={pending}
          onClick={() => void handleRate('down')}
          className={thumbClass('down')}
        >
          <ThumbsDown className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Feedback details"
          className={`absolute z-50 w-[22rem] max-w-[calc(100vw-2rem)] ${
            align === 'left' ? 'left-0' : 'right-0'
          } ${placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}`}
        >
          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-lg ring-1 ring-black/5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {rating === 'up' ? 'What was particularly good?' : 'What went wrong?'}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  Your vote is saved. Details below are optional.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="text-gray-400 transition-colors hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {selections.length > 0 && (
              <ul className="space-y-3">
                {selections.map((selection) => {
                  const field = fieldsByKey.get(selection.key);
                  if (!field) return null;
                  return (
                    <li key={selection.key} className="rounded-lg border border-gray-100 bg-gray-50 p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-700">{field.label}</p>
                          {field.value && (
                            <p className="mt-0.5 line-clamp-2 break-words text-xs text-gray-500">
                              {field.value}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <button
                            type="button"
                            aria-label={`${field.label} was good`}
                            aria-pressed={selection.verdict === 'good'}
                            onClick={() => updateSelection(selection.key, { verdict: 'good' })}
                            className={verdictClass(selection.verdict === 'good', 'good')}
                          >
                            <ThumbsUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`${field.label} was bad`}
                            aria-pressed={selection.verdict === 'bad'}
                            onClick={() => updateSelection(selection.key, { verdict: 'bad' })}
                            className={verdictClass(selection.verdict === 'bad', 'bad')}
                          >
                            <ThumbsDown className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${field.label}`}
                            onClick={() => removeSelection(selection.key)}
                            className="ml-0.5 flex h-6 w-6 items-center justify-center rounded text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      {selection.verdict === 'bad' && (
                        <textarea
                          rows={2}
                          maxLength={MAX_TEXT_LENGTH}
                          value={selection.comment}
                          onChange={(e) => updateSelection(selection.key, { comment: e.target.value })}
                          placeholder={field.badPrompt ?? 'What should it have been?'}
                          className="mt-2 w-full resize-y rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {groupedOptions.length > 0 && selections.length < MAX_FIELDS && (
              <label className="block">
                <span className="text-xs font-medium text-gray-600">
                  {selections.length === 0 ? 'Anything stand out?' : 'Add another field'}
                </span>
                <select
                  value=""
                  onChange={(e) => addSelection(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="" disabled>
                    Pick a field…
                  </option>
                  {groupedOptions.map(([group, groupFields]) => (
                    <optgroup key={group} label={group}>
                      {groupFields.map((field) => (
                        <option key={field.key} value={field.key}>
                          {optionText(field)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            )}

            {asksExpectedRecord && (
              <label className="block">
                <span className="text-xs font-medium text-gray-600">
                  Record you expected (optional)
                </span>
                <input
                  type="text"
                  value={expectedRecord}
                  onChange={(e) => setExpectedRecord(e.target.value)}
                  placeholder="Paste a record link or ID"
                  className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </label>
            )}

            <label className="block">
              <span className="text-xs font-medium text-gray-600">What could be improved?</span>
              <textarea
                rows={3}
                maxLength={MAX_TEXT_LENGTH}
                value={improvement}
                onChange={(e) => setImprovement(e.target.value)}
                className="mt-1 w-full resize-y rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </label>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={!selfHref || pending}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
